/**
 * ハイドレートが完了せず、サーバが出した fallback の HTML だけが画面に残るクラスの保険。
 *
 * なぜインライン script なのか（ここが設計の核心）:
 * `app/loading.tsx` の従来のリロードは `useEffect` の中にある。effect は React が
 * commit したときにしか走らないため、**commit されない状況＝まさに保険が要る状況では
 * 起動しない**。2026-08-22 の実測（/catalog）では readyState が complete・
 * ネットワークも全 200 なのに、45 秒経っても effect が1つも走らず DOM は fallback のまま
 * だった。インライン script は**パース時点で実行される**ので、この状況でも動く。
 *
 * 安全設計（`app/loading.tsx` は全ルート共有なので、正常系を1ミリも変えないことが最優先）:
 * - commit されたら `HYDRATED_FLAG` が立ち、ウォッチドッグは発火時に**何もせず終わる**
 * - リロードは**1 回だけ**。2 回目は文言を出して止まる（無限リロードで自分を悪化させない）
 * - `sessionStorage` が使えなくても例外を投げない（プライベートモード等）
 *
 * 機構: `__tests__/loading-watchdog.test.ts`
 */

/** commit 済みを表すグローバルフラグ。`app/loading.tsx` の effect が立てる。 */
export const HYDRATED_FLAG = '__gforceHydrated'

/** ウォッチドッグの二重仕掛け防止フラグ。 */
export const WATCHDOG_FLAG = '__gforceLoadingWatchdog'

/** 発火までの待ち時間。従来の effect 版と同じ 20 秒（正常でも遅い画面で誤発火させない）。 */
export const LOADING_WATCHDOG_DELAY_MS = 20_000

/** 復帰不能を伝える要素の id。既定は hidden で、ウォッチドッグが最後の手段として表示する。 */
export const LOADING_STUCK_MESSAGE_ID = 'gforce-loading-stuck'

/** リロード回数を持つキー。タブを閉じれば消える（sessionStorage）。 */
export const LOADING_RETRY_STORAGE_KEY = 'gforce-loading-retry'

/**
 * `<script>{LOADING_WATCHDOG_SCRIPT}</script>` として埋め込むテキスト。
 *
 * React 19 は script の文字列 children を**エスケープせずそのまま出力する**ため、
 * ここに動的な値・利用者入力を混ぜてはならない（すべて静的リテラルであること）。
 * `</script` を含めるとその場で HTML が壊れるので、テストで固定している。
 */
export const LOADING_WATCHDOG_SCRIPT = `(function(){
if(window.${WATCHDOG_FLAG})return;
window.${WATCHDOG_FLAG}=1;
setTimeout(function(){
if(window.${HYDRATED_FLAG})return;
var n=0;
try{n=parseInt(window.sessionStorage.getItem('${LOADING_RETRY_STORAGE_KEY}')||'0',10)||0}catch(e){}
if(n<1){try{window.sessionStorage.setItem('${LOADING_RETRY_STORAGE_KEY}',String(n+1))}catch(e){}window.location.reload();return}
var el=document.getElementById('${LOADING_STUCK_MESSAGE_ID}');
if(el)el.hidden=false;
},${LOADING_WATCHDOG_DELAY_MS});
})();`
